"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import LeaderboardContents from "@/components/LeaderboardContents";
import { WheelOption, isMainWheelName } from "@/data/wheels";
import { LeaderboardBoard } from "@/lib/rng";
import { submitScore } from "@/lib/leaderboard";
import { useAuth } from "@/hooks/useAuth";
import { hasGuestShared, markGuestShared } from "@/lib/guestTokens";
import { REROLL_COOLDOWN_MS, formatCountdown } from "@/lib/reroll";
import {
  MAX_SINGLE_WHEEL_REROLLS,
  MULTIPLAYER_SUBMIT_COST,
  REROLL_TOKEN_COST,
  SINGLE_WHEEL_REROLL_COST,
  claimShareBonus,
  spendTokens,
} from "@/lib/tokens";

export interface SpinResult {
  wheelName: string;
  option: WheelOption;
}

interface ResultsSummaryProps {
  results: SpinResult[];
  chaosResults: SpinResult[];
  fightResults: SpinResult[];
  onPickFight: () => void;
  onReroll: () => void;
  // Opens the in-game tokens panel. When omitted, the "Get tokens" link falls
  // back to navigating to /tokens.
  onGetTokens?: () => void;
  onSubmitted: (scoreId: string) => void;
  // Set once this run has already been submitted (persisted), so returning to
  // the results screen after navigating away shows the submitted state instead
  // of offering a duplicate submission.
  submittedScoreId: string | null;
  totalScore: number;
  rank: string;
  alignmentLabel: string | null;
  leaderboardBoard: LeaderboardBoard | null;
  isSidekick: boolean;
  hasCrown: boolean;
  // When this run reached the results screen (ms epoch), or null. Source of
  // truth for the reroll cooldown — persisted in run state so it survives a
  // refresh and resets on a new run.
  cooldownStartedAt: number | null;
  // A "vs friend" run. These can't be submitted to the leaderboard for free —
  // submission costs MULTIPLAYER_SUBMIT_COST tokens. Solo runs are unaffected.
  isMultiplayer?: boolean;
  // Returns to the welcome/menu screen (Play vs Friend, Demo, etc.). Omitted in
  // demo mode, which has its own "Play for Real" exit.
  onBackToMenu?: () => void;
  // Demo run: show the full stats card but with NO submission / reroll /
  // monetization — just "Roll Again" and "Play for Real".
  demo?: boolean;
  onDemoRollAgain?: () => void;
  onDemoPlayForReal?: () => void;
  // Single-wheel reroll: re-spins one main-stat result in place. Charged here,
  // applied by the parent. `wheelRerollsUsed` drives the per-run cap display.
  onRerollWheel?: (index: number) => void;
  wheelRerollsUsed?: number;
}

export default function ResultsSummary({
  results,
  chaosResults,
  fightResults,
  onPickFight,
  onReroll,
  onGetTokens,
  onSubmitted,
  submittedScoreId,
  totalScore,
  rank,
  alignmentLabel,
  leaderboardBoard,
  isSidekick,
  hasCrown,
  cooldownStartedAt,
  isMultiplayer = false,
  onBackToMenu,
  demo = false,
  onDemoRollAgain,
  onDemoPlayForReal,
  onRerollWheel,
  wheelRerollsUsed = 0,
}: ResultsSummaryProps) {
  const { user, profile, applyTokenBalance, addGuestTokens } = useAuth();
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >(submittedScoreId ? "success" : "idle");
  const [errorMsg, setErrorMsg] = useState("");
  // The submitted score's id, needed to build its shareable /result/[id] link.
  // Seeded from the persisted run so a returning, already-submitted run keeps
  // its share link and submitted state.
  const [scoreId, setScoreId] = useState<string | null>(submittedScoreId);

  const isGuest = !user;

  // Pre-fill the leaderboard entry with the player's account name, but let
  // them give this particular character a different name if they want.
  useEffect(() => {
    if (!nameTouched && profile?.username) setName(profile.username);
  }, [nameTouched, profile]);

  // Actually records the score to the leaderboard. Shared by the free (solo)
  // path and the paid (multiplayer) path — the latter spends tokens first.
  const performSubmit = async () => {
    const displayName = name.trim() || profile?.username || "";
    if (!displayName || !leaderboardBoard) return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      const id = await submitScore(
        {
          name: displayName,
          score: totalScore,
          rank,
          board: leaderboardBoard,
          alignmentLabel: alignmentLabel ?? "",
          isSidekick,
          hasCrown,
          rolledResults: [...results, ...chaosResults, ...fightResults],
          userId: user?.id,
        },
        isGuest,
      );
      setScoreId(id);
      setStatus("success");
      onSubmitted(id);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  // === Multiplayer paid submission ===
  // A "vs friend" run can only be recorded after spending tokens. We charge
  // first (the anti-abuse order from the spec) and then submit.
  const [showMpModal, setShowMpModal] = useState(false);
  const [mpStatus, setMpStatus] = useState<"idle" | "spending" | "error">(
    "idle",
  );
  const canAffordMpSubmit =
    !!user && (profile?.tokens ?? 0) >= MULTIPLAYER_SUBMIT_COST;

  const handleMpConfirm = async () => {
    if (mpStatus === "spending") return;
    setMpStatus("spending");
    try {
      const balance = await spendTokens(
        MULTIPLAYER_SUBMIT_COST,
        "multiplayer_submission",
      );
      applyTokenBalance(balance);
      setShowMpModal(false);
      setMpStatus("idle");
      await performSubmit();
    } catch {
      setMpStatus("error");
    }
  };

  // === Sharing ===
  const [shareMsg, setShareMsg] = useState("");
  const [sharing, setSharing] = useState(false);

  // === Inline leaderboard (opens right under the View Leaderboard button) ===
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const leaderboardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showLeaderboard) return;
    const onDown = (e: MouseEvent) => {
      // Click anywhere outside the button+panel closes it. The button lives
      // inside this ref, so its own click toggles via onClick instead.
      if (
        leaderboardRef.current &&
        !leaderboardRef.current.contains(e.target as Node)
      ) {
        setShowLeaderboard(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showLeaderboard]);

  const handleShare = async () => {
    if (!scoreId || sharing) return;
    setSharing(true);
    const url = `${window.location.origin}/result/${scoreId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard can be unavailable (insecure context); the link is still
      // shown below so the player can copy it manually.
    }

    if (isGuest) {
      // Guests earn the share bonus locally too — once per result, mirroring
      // the server-side once-per-score rule for signed-in players.
      if (scoreId && !hasGuestShared(scoreId)) {
        markGuestShared(scoreId);
        addGuestTokens(5);
        setShareMsg("Link copied! +5 tokens");
      } else {
        setShareMsg("Link copied!");
      }
      setSharing(false);
      return;
    }

    try {
      const { awarded, balance } = await claimShareBonus(scoreId);
      applyTokenBalance(balance);
      setShareMsg(
        awarded
          ? "Link copied! +5 tokens"
          : "Already shared — no bonus this time",
      );
    } catch {
      setShareMsg("Link copied!");
    }
    setSharing(false);
  };

  // === Reroll cooldown ===
  // Derived entirely from the persisted cooldownStartedAt, with a 1s tick to
  // re-render the countdown. No writes happen here, so a refresh resumes the
  // same countdown instead of restarting it.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (cooldownStartedAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownStartedAt]);

  // Until the run stamps its cooldown (a frame after mount), treat it as fully
  // locked rather than free, so "Reroll Free" can never flash on screen.
  const remainingMs =
    cooldownStartedAt === null
      ? REROLL_COOLDOWN_MS
      : Math.max(0, REROLL_COOLDOWN_MS - (now - cooldownStartedAt));
  const rerollLocked = remainingMs > 0;

  const tokens = profile?.tokens ?? 0;
  const canAffordReroll = !!user && tokens >= REROLL_TOKEN_COST;

  const [showSpendModal, setShowSpendModal] = useState(false);
  const [spendStatus, setSpendStatus] = useState<
    "idle" | "spending" | "error"
  >("idle");

  // Paid early reroll: deduct 100 tokens atomically server-side, and only on
  // success tear down the current run and start a fresh one.
  const handleConfirmSpend = async () => {
    if (spendStatus === "spending") return;
    setSpendStatus("spending");
    try {
      const balance = await spendTokens(REROLL_TOKEN_COST, "early_reroll");
      applyTokenBalance(balance);
      setShowSpendModal(false);
      setSpendStatus("idle");
      onReroll();
    } catch {
      setSpendStatus("error");
    }
  };

  // === Single-wheel (targeted) reroll ===
  // Re-spin one main stat for a small flat cost, capped per run. Only offered
  // before the run is submitted (changing the build afterwards wouldn't touch
  // the recorded score) and only to signed-in players (the spend is a server
  // RPC). Guests and demo runs never see it.
  const rerollsLeft = Math.max(0, MAX_SINGLE_WHEEL_REROLLS - wheelRerollsUsed);
  const singleRerollActive =
    !demo &&
    !isMultiplayer &&
    !!onRerollWheel &&
    !!user &&
    status !== "success" &&
    !submittedScoreId;
  const canAffordSingle = tokens >= SINGLE_WHEEL_REROLL_COST;
  const [rerollIndex, setRerollIndex] = useState<number | null>(null);
  const [singleStatus, setSingleStatus] = useState<
    "idle" | "spending" | "error"
  >("idle");
  // Briefly outlines the row that just changed, so the reroll is noticeable.
  const [flashIndex, setFlashIndex] = useState<number | null>(null);

  const handleConfirmSingleReroll = async () => {
    if (rerollIndex === null || singleStatus === "spending") return;
    setSingleStatus("spending");
    try {
      const balance = await spendTokens(
        SINGLE_WHEEL_REROLL_COST,
        "single_wheel_reroll",
      );
      applyTokenBalance(balance);
      const idx = rerollIndex;
      onRerollWheel?.(idx);
      setRerollIndex(null);
      setSingleStatus("idle");
      setFlashIndex(idx);
      setTimeout(() => setFlashIndex((f) => (f === idx ? null : f)), 700);
    } catch {
      setSingleStatus("error");
    }
  };

  return (
    <div className="w-full max-w-2xl animate-fade-in rounded-2xl border border-accent/40 bg-white/5 p-6 sm:p-10">
      {/* Escape hatch back to the welcome screen (Play vs Friend, Demo, etc.).
          Demo has its own "Play for Real" exit, so it's omitted there. */}
      {!demo && onBackToMenu && (
        <button
          onClick={onBackToMenu}
          className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white/70 transition hover:border-accent hover:text-accent"
        >
          ← Menu
        </button>
      )}

      {/* 1 — Name the character + when the next free spin is available. This
          comes first so saving to the leaderboard is the headline action.
          Hidden entirely in demo mode — a demo can't be submitted at all. */}
      {!demo && (
        <>
      <h2 className="mb-6 text-center text-[clamp(1.25rem,3vw,1.875rem)] font-extrabold">
        Save Your Character
      </h2>

      {leaderboardBoard ? (
        status === "success" ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="text-lg font-bold text-green-400">
              Recorded to the leaderboard. Nice run,{" "}
              {name.trim() || profile?.username || "hero"}!
            </div>
            {isGuest && (
              <div className="max-w-md text-sm text-white/50">
                Your result disappears in 12 hours.{" "}
                <Link href="/login" className="font-bold text-accent hover:underline">
                  Create a profile
                </Link>{" "}
                to keep it permanently.
              </div>
            )}

            {/* Share for tokens */}
            {scoreId && (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  className="rounded-full bg-accent px-[clamp(1.5rem,3vw,2.25rem)] py-[clamp(0.5rem,1.2vh,0.75rem)] text-[clamp(0.7rem,1.3vw,0.875rem)] font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Share Result — +5 Tokens
                </button>
                {shareMsg && (
                  <div className="animate-fade-in rounded-full border border-green-400/40 bg-green-400/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-green-400">
                    {shareMsg}
                  </div>
                )}
                {isGuest && (
                  <div className="text-xs text-white/40">
                    <Link href="/login" className="font-bold text-accent hover:underline">
                      Sign in
                    </Link>{" "}
                    for +25 tokens and daily rewards
                  </div>
                )}
              </div>
            )}

            <div
              ref={leaderboardRef}
              className="flex w-full flex-col items-center gap-3"
            >
              <button
                onClick={() => setShowLeaderboard((s) => !s)}
                className={`rounded-full border-2 px-[clamp(1.5rem,3vw,2.25rem)] py-[clamp(0.5rem,1.2vh,0.75rem)] text-[clamp(0.7rem,1.3vw,0.875rem)] font-bold uppercase tracking-widest transition ${
                  showLeaderboard
                    ? "border-accent bg-accent text-white"
                    : "border-accent text-accent hover:bg-accent hover:text-white"
                }`}
              >
                {showLeaderboard ? "Hide Leaderboard" : "View Leaderboard"}
              </button>
              {showLeaderboard && (
                <div className="animate-slide-down flex max-h-96 w-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/15 bg-background/80 text-left">
                  <LeaderboardContents maxEntries={10} />
                </div>
              )}
            </div>
          </div>
        ) : isMultiplayer ? (
          // Multiplayer ("vs friend") runs cost tokens to submit. Guests have
          // no submission path at all — they're nudged to sign in first.
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="max-w-md rounded-xl border border-yellow-400/40 bg-yellow-400/10 px-4 py-3 text-sm font-semibold text-yellow-200">
              Multiplayer runs cannot be submitted for free.
            </div>
            {isGuest ? (
              <div className="max-w-md text-sm text-white/60">
                <Link href="/login" className="font-bold text-accent hover:underline">
                  Sign in
                </Link>{" "}
                and get {MULTIPLAYER_SUBMIT_COST} tokens to submit this score.
              </div>
            ) : (
              <>
                <label className="text-sm uppercase tracking-widest text-white/40">
                  Name this character for the leaderboard
                </label>
                <input
                  type="text"
                  value={name}
                  maxLength={24}
                  onChange={(e) => {
                    setNameTouched(true);
                    setName(e.target.value);
                  }}
                  placeholder="Your name"
                  className="w-full max-w-md rounded-full border border-white/15 bg-black/40 px-5 py-3 text-center text-lg font-semibold text-white outline-none focus:border-accent"
                />
                {canAffordMpSubmit ? (
                  <button
                    onClick={() => {
                      setMpStatus("idle");
                      setShowMpModal(true);
                    }}
                    disabled={!name.trim() || status === "submitting"}
                    className="rounded-full bg-accent px-8 py-3 text-base font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {status === "submitting"
                      ? "Saving..."
                      : `Submit to Leaderboard — ${MULTIPLAYER_SUBMIT_COST} tokens`}
                  </button>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <button
                      disabled
                      className="cursor-not-allowed rounded-full border-2 border-white/15 px-8 py-3 text-base font-bold uppercase tracking-widest text-white/30"
                    >
                      Submit to Leaderboard — {MULTIPLAYER_SUBMIT_COST} tokens
                    </button>
                    <div className="text-sm font-semibold text-red-400">
                      Not enough tokens
                    </div>
                    {onGetTokens ? (
                      <button
                        onClick={onGetTokens}
                        className="text-xs font-bold uppercase tracking-widest text-accent hover:underline"
                      >
                        Get tokens
                      </button>
                    ) : (
                      <Link
                        href="/tokens"
                        className="text-xs font-bold uppercase tracking-widest text-accent hover:underline"
                      >
                        Get tokens
                      </Link>
                    )}
                  </div>
                )}
                {status === "error" && (
                  <div className="text-center text-sm text-red-400">
                    Couldn&apos;t save your score: {errorMsg}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <label className="text-sm uppercase tracking-widest text-white/40">
              {isGuest
                ? "Enter your name to record your score"
                : "Name this character for the leaderboard"}
            </label>
            <div className="flex w-full max-w-md flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={name}
                maxLength={24}
                onChange={(e) => {
                  setNameTouched(true);
                  setName(e.target.value);
                }}
                placeholder="Your name"
                className="flex-1 rounded-full border border-white/15 bg-black/40 px-5 py-3 text-center text-lg font-semibold text-white outline-none focus:border-accent sm:text-left"
              />
              <button
                onClick={performSubmit}
                disabled={!name.trim() || status === "submitting"}
                className="rounded-full bg-accent px-8 py-3 text-base font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === "submitting" ? "Saving..." : "Submit"}
              </button>
            </div>
            {isGuest && (
              <div className="max-w-md text-center text-sm text-white/50">
                Your result disappears in 12 hours.{" "}
                <Link href="/login" className="font-bold text-accent hover:underline">
                  Create a profile
                </Link>{" "}
                to keep it permanently.
              </div>
            )}
            {status === "error" && (
              <div className="text-center text-sm text-red-400">
                Couldn&apos;t save your score: {errorMsg}
              </div>
            )}
          </div>
        )
      ) : null}
        </>
      )}

      {/* Time remaining before the next free spin (reroll) is available.
          Demo runs have no cooldown / monetization, so this is hidden. */}
      {!demo && (
      <div className="mt-6 text-center">
        {rerollLocked ? (
          <>
            <div className="text-sm uppercase tracking-widest text-white/40">
              Next free spin in
            </div>
            <div className="mt-1 font-mono text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold">
              {formatCountdown(remainingMs)}
            </div>
          </>
        ) : (
          <div className="text-sm font-bold uppercase tracking-widest text-green-400">
            Your next free spin is ready
          </div>
        )}
      </div>
      )}

      {/* 2 — The stats: alignment, the full build, chaos events, final score.
          These always show — they're the whole point of a demo run too. */}
      <div className="mt-8 border-t border-white/10 pt-6">
        {hasCrown && (
          <div className="mb-4 text-center">
            <div className="inline-block rounded-full border border-yellow-400/60 px-4 py-1 text-xs font-bold uppercase tracking-widest text-yellow-300">
              👑 Crowned
            </div>
          </div>
        )}
        {alignmentLabel && (
          <div className="mb-4 text-center">
            <div
              className={`inline-block rounded-full border px-4 py-1 text-xs font-bold uppercase tracking-widest ${
                alignmentLabel.toLowerCase().includes("villain")
                  ? "border-red-500/50 text-red-400"
                  : "border-accent text-accent"
              }`}
            >
              {alignmentLabel}
            </div>
          </div>
        )}
        <div className="mb-6 text-center">
          <h2 className="text-[clamp(1.25rem,3vw,1.875rem)] font-extrabold">
            Final Build
          </h2>
          {singleRerollActive && (
            <p className="mt-1 text-xs text-white/40">
              {rerollsLeft > 0 ? (
                <>
                  Stuck with a bad roll? Reroll one stat for{" "}
                  {SINGLE_WHEEL_REROLL_COST} tokens —{" "}
                  <span className="text-white/60">{rerollsLeft} left</span>
                </>
              ) : (
                <>You&apos;ve used all your stat rerolls this run</>
              )}
            </p>
          )}
        </div>
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {results.map((r, i) => {
            const isConsolationWeapon = r.wheelName === "Consolation Weapon";
            const canRerollRow =
              singleRerollActive &&
              rerollsLeft > 0 &&
              isMainWheelName(r.wheelName);
            const flashing = flashIndex === i;
            return (
              <div
                key={`${r.wheelName}-${i}`}
                className={`flex items-center justify-between gap-2 rounded-lg px-4 py-2 transition ${
                  isConsolationWeapon
                    ? "border border-yellow-400/60 bg-yellow-400/10"
                    : "bg-black/30"
                } ${flashing ? "ring-2 ring-accent" : ""}`}
              >
                <div className="min-w-0">
                  <div
                    className={`text-xs uppercase tracking-widest ${
                      isConsolationWeapon ? "text-yellow-300/70" : "text-white/40"
                    }`}
                  >
                    {r.wheelName}
                    {isConsolationWeapon && (
                      <span className="ml-2 inline-block rounded-full border border-yellow-400/60 px-2 py-0.5 text-[0.6rem] font-bold text-yellow-300">
                        2x Points
                      </span>
                    )}
                  </div>
                  <div className="font-semibold">{r.option.name}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <div
                    className={`font-mono font-bold ${
                      isConsolationWeapon
                        ? "text-yellow-300"
                        : r.option.value >= 0
                          ? "text-green-400"
                          : "text-red-400"
                    }`}
                  >
                    {r.option.value >= 0 ? "+" : ""}
                    {r.option.value.toLocaleString()}
                  </div>
                  {canRerollRow && (
                    <button
                      onClick={() => {
                        setSingleStatus("idle");
                        setRerollIndex(i);
                      }}
                      title={`Reroll ${r.wheelName} for ${SINGLE_WHEEL_REROLL_COST} tokens`}
                      className="inline-flex items-center gap-1 rounded-full border border-accent/50 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest text-accent transition hover:bg-accent hover:text-white"
                    >
                      ↻ {SINGLE_WHEEL_REROLL_COST}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {chaosResults.length > 0 && (
          <div className="mb-8">
            <h3 className="mb-3 text-center text-sm font-bold uppercase tracking-[0.3em] text-yellow-300">
              Chaos Events
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {chaosResults.map((r, i) => (
                <div
                  key={`${r.wheelName}-${i}`}
                  className="flex items-center justify-between rounded-lg border border-yellow-400/30 bg-black/30 px-4 py-2"
                >
                  <div>
                    <div className="text-xs uppercase tracking-widest text-yellow-300/70">
                      {r.wheelName}
                    </div>
                    <div className="font-semibold">{r.option.name}</div>
                  </div>
                  <div
                    className={`font-mono font-bold ${
                      r.option.value >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {r.option.value >= 0 ? "+" : ""}
                    {r.option.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="border-t border-white/10 pt-6 text-center">
          <div className="text-sm uppercase tracking-widest text-white/40">
            Final Score
          </div>
          <div className="mt-1 text-[clamp(2rem,6vw,3rem)] font-extrabold">
            {totalScore.toLocaleString()}
          </div>
          <div className="mt-4 inline-block rounded-full bg-accent px-6 py-2 text-[clamp(1rem,2.5vw,1.5rem)] font-bold uppercase tracking-widest">
            {rank}
          </div>
          {leaderboardBoard && (
            <div className="mt-4 text-sm uppercase tracking-widest text-white/40">
              For the{" "}
              <span className="text-accent">
                {leaderboardBoard === "hero" ? "Hero" : "Villain"} Leaderboard
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 3 — Everything else: the bonus round and the reroll action. All of
          this (and any token spend) is suppressed for a demo run. */}
      {!demo && (
        <>
      <div className="mt-6 border-t border-white/10 pt-6 text-center">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.3em] text-white/40">
          Bonus
        </h3>
        {fightResults.length === 2 && (
          <div className="mx-auto mb-4 max-w-md space-y-2">
            <div className="rounded-lg bg-black/30 px-4 py-2">
              <div className="text-xs uppercase tracking-widest text-white/40">
                You Fight
              </div>
              <div className="font-semibold">{fightResults[0].option.name}</div>
            </div>
            <div className="rounded-lg bg-black/30 px-4 py-2">
              <div className="text-xs uppercase tracking-widest text-white/40">
                Do You Win?
              </div>
              <div
                className={`font-bold ${
                  fightResults[1].option.name === "Yes"
                    ? "text-green-400"
                    : "text-red-400"
                }`}
              >
                {fightResults[1].option.name}
              </div>
            </div>
          </div>
        )}
        <button
          onClick={onPickFight}
          className="rounded-full border-2 border-accent px-[clamp(1.5rem,3vw,2.25rem)] py-[clamp(0.5rem,1.2vh,0.75rem)] text-[clamp(0.7rem,1.3vw,0.875rem)] font-bold uppercase tracking-widest text-accent transition hover:bg-accent hover:text-white"
        >
          {fightResults.length === 2 ? "Fight Again" : "Pick a Fight"}
        </button>
      </div>

      {/* Reroll action. The countdown itself lives up top with the save panel,
          so here we only surface the action (free when unlocked, or paid). */}
      <div className="mt-6 border-t border-white/10 pt-6 text-center">
        {rerollLocked ? (
          canAffordReroll ? (
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => {
                  setSpendStatus("idle");
                  setShowSpendModal(true);
                }}
                className="rounded-full border-2 border-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-accent transition hover:bg-accent hover:text-white"
              >
                Reroll now — {REROLL_TOKEN_COST} tokens
              </button>
              <div className="text-xs uppercase tracking-widest text-white/40">
                Or wait for your free spin above
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <button
                disabled
                className="cursor-not-allowed rounded-full border-2 border-white/15 px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-white/30"
              >
                Reroll now — {REROLL_TOKEN_COST} tokens
              </button>
              <div className="text-sm font-semibold text-red-400">
                Not enough tokens
              </div>
              {onGetTokens ? (
                <button
                  onClick={onGetTokens}
                  className="text-xs font-bold uppercase tracking-widest text-accent hover:underline"
                >
                  Get tokens
                </button>
              ) : (
                <Link
                  href="/tokens"
                  className="text-xs font-bold uppercase tracking-widest text-accent hover:underline"
                >
                  Get tokens
                </Link>
              )}
            </div>
          )
        ) : (
          <button
            onClick={onReroll}
            className="rounded-full bg-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest transition hover:brightness-110"
          >
            Reroll Free
          </button>
        )}
      </div>
        </>
      )}

      {/* Demo run actions — purely for fun, no tokens or leaderboard. */}
      {demo && (
        <div className="mt-6 flex flex-col items-center gap-3 border-t border-white/10 pt-6 text-center sm:flex-row sm:justify-center">
          <button
            onClick={onDemoRollAgain}
            className="rounded-full bg-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-white transition hover:brightness-110"
          >
            Roll Again
          </button>
          <button
            onClick={onDemoPlayForReal}
            className="rounded-full border-2 border-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-accent transition hover:bg-accent hover:text-white"
          >
            Play for Real
          </button>
        </div>
      )}

      {/* Spend-to-reroll confirmation modal */}
      {showSpendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-accent/40 bg-background p-6 text-center sm:p-8">
            <h3 className="text-lg font-extrabold">
              Spend {REROLL_TOKEN_COST} tokens to reroll now?
            </h3>
            <p className="mt-2 text-sm text-white/60">
              Your current run will be gone.
            </p>
            {spendStatus === "error" && (
              <p className="mt-3 text-sm text-red-400">
                Couldn&apos;t spend tokens. Please try again.
              </p>
            )}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleConfirmSpend}
                disabled={spendStatus === "spending"}
                className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {spendStatus === "spending"
                  ? "Spending..."
                  : `Spend ${REROLL_TOKEN_COST}`}
              </button>
              <button
                onClick={() => {
                  setShowSpendModal(false);
                  setSpendStatus("idle");
                }}
                disabled={spendStatus === "spending"}
                className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multiplayer paid-submission confirmation modal */}
      {showMpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-accent/40 bg-background p-6 text-center sm:p-8">
            <h3 className="text-lg font-extrabold">
              Spend {MULTIPLAYER_SUBMIT_COST} tokens to submit this score to the
              leaderboard?
            </h3>
            {mpStatus === "error" && (
              <p className="mt-3 text-sm text-red-400">
                Couldn&apos;t submit. Please try again.
              </p>
            )}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleMpConfirm}
                disabled={mpStatus === "spending"}
                className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {mpStatus === "spending"
                  ? "Submitting..."
                  : `Spend ${MULTIPLAYER_SUBMIT_COST}`}
              </button>
              <button
                onClick={() => {
                  setShowMpModal(false);
                  setMpStatus("idle");
                }}
                disabled={mpStatus === "spending"}
                className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single-wheel reroll confirmation modal */}
      {rerollIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-accent/40 bg-background p-6 text-center sm:p-8">
            <h3 className="text-lg font-extrabold">
              Reroll {results[rerollIndex]?.wheelName}?
            </h3>
            <p className="mt-2 text-sm text-white/60">
              Spin it again for {SINGLE_WHEEL_REROLL_COST} tokens. The new roll
              replaces this one — even if it comes out worse.
            </p>
            <p className="mt-1 text-xs text-white/40">
              {rerollsLeft - 1} reroll{rerollsLeft - 1 === 1 ? "" : "s"} left
              after this
            </p>
            {singleStatus === "error" && (
              <p className="mt-3 text-sm text-red-400">
                Couldn&apos;t reroll. Please try again.
              </p>
            )}
            {canAffordSingle ? (
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <button
                  onClick={handleConfirmSingleReroll}
                  disabled={singleStatus === "spending"}
                  className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {singleStatus === "spending"
                    ? "Rolling..."
                    : `Spend ${SINGLE_WHEEL_REROLL_COST}`}
                </button>
                <button
                  onClick={() => {
                    setRerollIndex(null);
                    setSingleStatus("idle");
                  }}
                  disabled={singleStatus === "spending"}
                  className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="text-sm font-semibold text-red-400">
                  Not enough tokens
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                  {onGetTokens ? (
                    <button
                      onClick={() => {
                        setRerollIndex(null);
                        onGetTokens();
                      }}
                      className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110"
                    >
                      Get tokens
                    </button>
                  ) : (
                    <Link
                      href="/tokens"
                      className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110"
                    >
                      Get tokens
                    </Link>
                  )}
                  <button
                    onClick={() => setRerollIndex(null)}
                    className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
